import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, prefersReducedMotion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

// Bumped each time a phone sheet opens while its options load, so a late response for an earlier tap is dropped
let loadingSheet = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {{ url: string, grid: Promise<Element | null> } | null} */
  #pending = null;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    // MLB speed: start loading the options when a finger touches Choose (or the mouse rests on it), not on click
    this.addEventListener('pointerdown', this.#prefetchOnPointer, { signal: this.#cartUpdateAbortController.signal });
    this.addEventListener('pointerover', this.#prefetchOnPointer, { signal: this.#cartUpdateAbortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /** @param {PointerEvent} event */
  #prefetchOnPointer = (event) => {
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element) || !event.target.closest('.quick-add__button--choose')) return;

    this.prefetch(this.productPageUrl);
  };

  /**
   * Loads the modal's content ahead of a click (the card's hover and variant changes call this too)
   * @param {string} url
   */
  prefetch(url) {
    if (this.dataset.usesSellingPlans === 'true') return;
    this.#loadProductGrid(url).catch(() => {});
  }

  /**
   * The product page's grid for the modal, fetched once per URL; a click during a prefetch shares its request
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(url) {
    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);
    if (this.#pending?.url === url) return this.#pending.grid;

    const pending = {
      url,
      grid: this.fetchProductPage(url)
        .then((html) => {
          const gridElement = html?.querySelector('[data-product-grid-content]');
          if (!gridElement) return null;
          // Cache the cloned element to avoid modifying the original
          const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(url, productGrid);
          return productGrid;
        })
        .finally(() => {
          if (this.#pending === pending) this.#pending = null;
        }),
    };
    this.#pending = pending;
    return pending.grid;
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // MLB speed: phones open the sheet at once with the card's photo, name and price while the options load
    if (!this.#cachedContent.has(currentUrl) && isMobileBreakpoint()) {
      await this.#openWhileLoading(currentUrl);
      return;
    }

    const productGrid = await this.#loadProductGrid(currentUrl);

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  /**
   * Opens the phone sheet with what the card already shows, then swaps in the options once the page arrives
   * @param {string} url
   */
  async #openWhileLoading(url) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    const dialog = dialogComponent instanceof QuickAddDialog ? dialogComponent.refs?.dialog : null;
    if (!(dialog instanceof HTMLDialogElement) || !modalContent) return;

    const ticket = ++loadingSheet;
    let closed = false;
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        closed = true;
        // Gone from view: its next opening gets the theme's own fade-ins again
        modalContent.removeAttribute('data-mlb-reveal');
      },
      { once: true }
    );
    // The shopper closed the sheet, or opened another card's, while this one loaded
    // (not dialog.open: the dialog only opens on the next frame, and a prefetch can finish first)
    const dismissed = () => closed || ticket !== loadingSheet || dialog.classList.contains('dialog-closing');

    modalContent.removeAttribute('data-mlb-reveal');
    modalContent.replaceChildren(this.#loadingContent());
    modalContent.setAttribute('aria-busy', 'true');
    this.#openQuickAddModal();

    /** @type {Element | null} */
    let productGrid = null;
    try {
      productGrid = await this.#loadProductGrid(url);
    } catch (error) {
      console.warn('[quick-add] Could not load product options:', error);
    }
    if (dismissed()) return;

    if (!productGrid) {
      modalContent.removeAttribute('aria-busy');
      window.location.href = url;
      return;
    }

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.#thumbnailReady(freshContent);
    if (dismissed()) return;

    const placeholders = modalContent.querySelector('.mlb-qa-skel');
    const { offsetTop = 0, offsetLeft = 0, offsetWidth = 0 } = placeholders instanceof HTMLElement ? placeholders : {};
    modalContent.toggleAttribute('data-mlb-reveal', true);
    modalContent.removeAttribute('aria-busy');
    modalContent.replaceChildren();
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    // The placeholders fade out over the options as they fade in, so there is no blank frame between them
    if (placeholders instanceof HTMLElement && !prefersReducedMotion()) {
      const opacity = getComputedStyle(placeholders).opacity;
      placeholders.classList.add('mlb-qa-skel--out');
      placeholders.style.cssText = `top: ${offsetTop}px; left: ${offsetLeft}px; width: ${offsetWidth}px;`;
      modalContent.append(placeholders);
      placeholders
        .animate({ opacity: [opacity, '0'] }, { duration: 240, easing: 'ease-out', fill: 'forwards' })
        .finished.then(
          () => placeholders.remove(),
          () => placeholders.remove()
        );
    }
  }

  /**
   * Stand-in sheet content: the card's photo, name and price, with soft placeholders where the options go
   * @returns {DocumentFragment}
   */
  #loadingContent() {
    const card = this.closest('product-card');
    const cardImage = card?.querySelector('slideshow-slide[aria-hidden="false"] img, slideshow-slide img, img');
    const fragment = document.createDocumentFragment();

    const media = document.createElement('div');
    media.className = 'mlb-qa-skel__media';
    if (cardImage instanceof HTMLImageElement && cardImage.complete && cardImage.naturalWidth) {
      // Same shape as the sheet's own photo: square, or the photo's own shape for hats (their gallery adapts)
      if (this.dataset.thumbAdapt === 'true') {
        media.style.setProperty('--mlb-qa-ratio', String(cardImage.naturalWidth / cardImage.naturalHeight));
      }
      const image = document.createElement('img');
      image.src = cardImage.currentSrc || cardImage.src;
      image.alt = cardImage.alt;
      media.append(image);
    }

    const header = document.createElement('div');
    header.className = 'product-header';
    const title = document.createElement('a');
    title.textContent = this.dataset.productTitle || '';
    title.href = this.productPageUrl;
    const price = document.createElement('div');
    price.className = 'mlb-qa-skel__price';
    const cardPrice = card?.querySelector('product-price [ref="priceContainer"]');
    if (cardPrice) price.append(...[...cardPrice.childNodes].map((node) => node.cloneNode(true)));
    header.append(title, price);

    const placeholders = document.createElement('div');
    placeholders.className = 'mlb-qa-skel';
    placeholders.setAttribute('aria-hidden', 'true');
    placeholders.innerHTML =
      '<span class="mlb-qa-skel__label"></span><span class="mlb-qa-skel__row"><span></span><span></span><span></span></span>' +
      '<span class="mlb-qa-skel__label"></span><span class="mlb-qa-skel__tiles">' +
      '<span></span>'.repeat(8) +
      '</span><span class="mlb-qa-skel__button"></span><span class="mlb-qa-skel__button"></span>';

    fragment.append(media, header, placeholders);
    return fragment;
  }

  /**
   * Waits (briefly) until the sheet's photo is decoded, so the stand-in photo is never swapped for a blank
   * @param {Element} productGrid
   */
  async #thumbnailReady(productGrid) {
    const source = productGrid.querySelector('.product-information__media img');
    if (!(source instanceof HTMLImageElement)) return;

    const image = new Image();
    image.sizes = source.getAttribute('sizes') || '';
    image.srcset = source.getAttribute('srcset') || '';
    image.src = source.getAttribute('src') || '';
    await Promise.race([image.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 500))]);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** The sheet's last laid-out height, 0 while closed */
  #sheetHeight = 0;
  /** @type {Animation | null} */
  #glide = null;
  #resizeObserver = new ResizeObserver(() => this.#glideToHeight());

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.dialog) this.#resizeObserver.observe(this.refs.dialog);

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#resizeObserver.disconnect();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * MLB: on phones the open sheet rises smoothly when it grows (its options arriving, Shopify's wallet button
   * adding "More payment options") instead of jumping up a frame later
   */
  #glideToHeight() {
    const { dialog } = this.refs;
    const height = dialog.open ? dialog.getBoundingClientRect().height : 0;
    const growth = height - this.#sheetHeight;
    const wasShowing = this.#sheetHeight > 0;
    this.#sheetHeight = height;
    if (!wasShowing || growth < 2 || !isMobileBreakpoint() || prefersReducedMotion()) return;

    // Carry on from wherever a glide still running has got to
    const offset = parseFloat(getComputedStyle(dialog).translate.split(' ')[1] ?? '0') || 0;
    this.#glide?.cancel();
    this.#glide = dialog.animate(
      { translate: [`0 ${offset + growth}px`, '0 0'] },
      { duration: 320, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
    );
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
